/**
 * The Arena's stand-in quote, and the only pricing arithmetic it is allowed.
 *
 * CONFIDENTIAL — ASI Internal.
 * Copyright 2026, Partnership Net; Agency for Sponsorship Intelligence, Lda;
 * Advanced Sponsorship Insights, GmbH. Confidential and proprietary.
 *
 * WHAT THIS IS FOR. The Partnership Net Pricing Engine owns every rate, every
 * discount and every sequencing rule (DEC-48). It is not live yet, so the Arena
 * has to put a number on a screen today — and the honest way to do that is a
 * clearly-labelled fallback that computes the least it can get away with, not a
 * second pricing model that will quietly disagree with the first one.
 *
 * So: quantity times unit fee, summed. No discount ladder, no volume threshold,
 * no refresh rate, no bundle logic. Those exist — `MmmRateCard` already carries
 * some of them — and they are deliberately NOT applied here, because a fallback
 * that reproduces half the engine's rules is worse than one that obviously does
 * not: the first looks right and is wrong, the second announces itself.
 *
 * LEAD TIME IS THE LONGEST LINE, not the sum. That is a simplification and it is
 * stated on the screen: real sequencing is the engine's (NEW-41 has Data
 * Recommendation queueing behind Data Confidence Score while Data Quality
 * Assurance runs alongside), and encoding a queue here would be inventing the
 * engine's scheduling model from the outside.
 *
 * Every quote this builds carries `source: 'arena-fallback'` and a provenance
 * line that says so, so a reader is never asked to guess which system priced it.
 */

import type {
  ServiceQuote,
  ServiceQuoteLine,
  ServiceScope,
  ServiceScopeLine,
} from '@/data/contracts';

/** One priced service in the Arena's stand-in table. */
export interface ServiceRate {
  readonly service: string;
  readonly label: string;
  readonly unitFee: number;
  readonly leadTimeDays: number;
}

export type ServiceRateTable = Readonly<Record<string, ServiceRate>>;

/** Sum of the line fees. Separate so the screen and the quote cannot disagree. */
export const quoteTotal = (lines: readonly ServiceQuoteLine[]): number =>
  lines.reduce((sum, l) => sum + l.fee, 0);

/**
 * The longest line, not the sum — see the note at the top of this file.
 *
 * Zero for an empty scope: an engagement with nothing in it takes no time, and
 * `Math.max()` of nothing is `-Infinity`, which would print.
 */
export const quoteLeadTime = (lines: readonly ServiceQuoteLine[]): number =>
  lines.length === 0 ? 0 : Math.max(...lines.map((l) => l.leadTimeDays));

/**
 * A line the Arena can price, or nothing.
 *
 * An unknown service key is dropped rather than priced at zero. Zero is a
 * number a reader believes; an absent line is one they notice. The caller
 * reports what was dropped — see `unpricedServices`.
 */
function priceLine(line: ServiceScopeLine, rates: ServiceRateTable): ServiceQuoteLine | null {
  const rate = rates[line.service];
  if (rate === undefined) return null;
  return {
    service: rate.service,
    label: rate.label,
    quantity: line.quantity,
    unitFee: rate.unitFee,
    fee: rate.unitFee * line.quantity,
    leadTimeDays: rate.leadTimeDays,
  };
}

/** Scope lines the stand-in table has no rate for. The engine will have them. */
export const unpricedServices = (scope: ServiceScope, rates: ServiceRateTable): readonly string[] =>
  scope.lines.filter((l) => rates[l.service] === undefined).map((l) => l.service);

/**
 * Build the fallback quote.
 *
 * `issuedAt` is injected rather than read from the clock, so a test asserts on a
 * fixed instant instead of on "roughly now" — and so a caller that needs the
 * quote and its record to carry the same timestamp can pass one.
 */
export function buildFallbackQuote(
  scope: ServiceScope,
  rates: ServiceRateTable,
  issuedAt: string,
): ServiceQuote {
  const lines = scope.lines.flatMap((l) => {
    const priced = priceLine(l, rates);
    return priced === null ? [] : [priced];
  });

  const missing = unpricedServices(scope, rates);
  const provenance =
    missing.length === 0
      ? 'Indicative — priced from the Arena’s approved stand-in rates, not from the Partnership Net Pricing Engine.'
      : `Indicative — priced from the Arena’s approved stand-in rates, not from the Partnership Net Pricing Engine. ${String(missing.length)} service(s) in this scope have no stand-in rate and are not included: ${missing.join(', ')}.`;

  return {
    // Derived from the scope, not random: the same scope quoted twice is the
    // same quote, so an approval cannot end up pointing at a quote nobody saw.
    quoteId: `fallback:${scope.agent}:${String(scope.lines.length)}:${issuedAt}`,
    agent: scope.agent,
    currency: scope.currency,
    lines,
    total: quoteTotal(lines),
    leadTimeDays: quoteLeadTime(lines),
    issuedAt,
    // No expiry, and that is the honest answer rather than a made-up window:
    // validity is a commercial policy and the engine owns it.
    expiresAt: null,
    source: 'arena-fallback',
    provenance,
  };
}
